import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import gsap from 'gsap';
import clsx from 'clsx';

const ToastContext = createContext(null);

const TONE_STYLES = {
  danger: 'border-danger/40 bg-danger-soft',
  warning: 'border-warning/40 bg-warning-soft',
  success: 'border-accent/40 bg-accent-soft',
  info: 'border-border bg-background-elevated',
};

const TONE_ICON = {
  danger: '⚠️',
  warning: '⚠️',
  success: '✅',
  info: 'ℹ️',
};

let idCounter = 0;

function ToastItem({ toast, onDismiss }) {
  const ref = useRef(null);

  useEffect(() => {
    gsap.fromTo(ref.current, { opacity: 0, y: -12, scale: 0.97 }, { opacity: 1, y: 0, scale: 1, duration: 0.25, ease: 'power2.out' });
  }, []);

  return (
    <div
      ref={ref}
      role="alert"
      className={clsx(
        'pointer-events-auto rounded-xl border px-4 py-3 shadow-card-hover backdrop-blur-sm flex items-start gap-3 w-full',
        TONE_STYLES[toast.tone] || TONE_STYLES.info
      )}
    >
      <span className="shrink-0 leading-none mt-0.5">{TONE_ICON[toast.tone] || TONE_ICON.info}</span>
      <div className="flex-1 min-w-0">
        {toast.title && <p className="text-sm font-semibold text-foreground">{toast.title}</p>}
        <p className="text-sm text-foreground-muted">{toast.message}</p>
        {toast.action && (
          <button
            type="button"
            onClick={() => {
              toast.action.onClick?.();
              onDismiss();
            }}
            className="mt-1.5 text-xs font-medium text-accent hover:underline"
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification"
        className="shrink-0 text-foreground-subtle hover:text-foreground transition-colors"
      >
        ✕
      </button>
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (toast) => {
      const id = ++idCounter;
      const duration = toast.duration ?? 7000;
      setToasts((current) => [...current, { id, tone: 'info', ...toast }]);
      if (duration > 0) {
        window.setTimeout(() => dismiss(id), duration);
      }
      return id;
    },
    [dismiss]
  );

  return (
    <ToastContext.Provider value={{ push, dismiss }}>
      {children}
      {createPortal(
        <div className="fixed z-[100] top-4 right-4 left-4 sm:left-auto sm:w-96 flex flex-col gap-2 pointer-events-none">
          {toasts.map((t) => (
            <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

/** { push({ tone: 'danger'|'warning'|'success'|'info', title?, message, action?: { label, onClick }, duration? }), dismiss(id) } */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}

export default ToastProvider;
